import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Avatar, Box, Button, Checkbox, CircularProgress, Popover, TextField, Typography } from '@mui/material';
import { getInitials } from 'utilities';
import { PersonSearchPopoverProps } from './PersonSearchPopover.props';

const PersonSearchPopover: React.FC<PersonSearchPopoverProps> = ({
  open,
  anchorEl,
  popoverId,
  onClose,
  searchTerm,
  onSearchTermChange,
  searching,
  onSearch,
  onKeyDown,
  results,
  selectionMode,
  selectedIds = [],
  onToggleSelection,
  onSave,
  onSelect,
  placeholder,
  helpText,
  minWidth = 300,
  showSearchIcon = false,
}) => (
  <Popover
    id={popoverId}
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}>
    <Box sx={{ p: 2, minWidth }}>
      <TextField
        fullWidth
        placeholder={placeholder}
        value={searchTerm}
        onChange={e => {
          onSearchTermChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        size="small"
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" fullWidth onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onSearch}
          startIcon={showSearchIcon ? <SearchIcon /> : undefined}
          disabled={!searchTerm.trim() || searching}>
          Search
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {helpText}
      </Typography>
      {searching ? (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        results.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {results.map(result => {
              const checked = selectedIds.includes(result.targetRecordId);
              return (
                <Box
                  key={result.targetRecordId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: selectionMode === 'single' ? 'pointer' : 'default',
                  }}
                  onClick={selectionMode === 'single' ? () => onSelect?.(result.targetRecordId) : undefined}>
                  {selectionMode === 'multi' && (
                    <Checkbox checked={checked} onChange={() => onToggleSelection?.(result.targetRecordId)} />
                  )}
                  <Avatar alt={result.name} src={result.imageSrc} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                    {getInitials({ name: result.name })}
                  </Avatar>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {result.name}
                  </Typography>
                </Box>
              );
            })}
            {selectionMode === 'multi' && (
              <Button variant="contained" fullWidth onClick={onSave} disabled={selectedIds.length === 0}>
                Save
              </Button>
            )}
          </Box>
        )
      )}
    </Box>
  </Popover>
);

export default PersonSearchPopover;
